import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  AlertTitle,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Assessment,
  Timeline,
  ShowChart,
  Speed,
  PlayArrow,
} from '@mui/icons-material';
import { AreaChart, Area, BarChart, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { format, parseISO } from 'date-fns';

import {
  riskAnalyticsService,
  RiskMetric,
  StressTestScenario,
  StressTestResult,
  RiskHeatmapData,
  MonteCarloSimulation,
  BacktestingResult,
  RiskAlert,
} from '../services/riskAnalyticsService';
import { LoggerService } from '../services/LoggerService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index} style={{ paddingTop: '24px' }}>
    {value === index && children}
  </div>
);

// Removed unused COLORS constant

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'low': return '#4caf50';
    case 'medium': return '#ff9800';
    case 'high': return '#f44336';
    case 'critical': return '#d32f2f';
    default: return '#757575';
  }
};

const getRiskLevelColor = (level: number) => {
  if (level < 3) return '#4caf50';
  if (level < 6) return '#ff9800';
  if (level < 8) return '#f44336';
  return '#d32f2f';
};

const EnhancedRiskAnalytics: React.FC = () => {
  // Theme removed as unused
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [riskMetrics, setRiskMetrics] = useState<RiskMetric[]>([]);
  const [stressScenarios, setStressScenarios] = useState<StressTestScenario[]>([]);
  const [heatmapData, setHeatmapData] = useState<RiskHeatmapData[]>([]);
  const [monteCarloResults, setMonteCarloResults] = useState<MonteCarloSimulation[]>([]);
  const [backtestingResults, setBacktestingResults] = useState<BacktestingResult[]>([]);
  const [selectedBacktest, setSelectedBacktest] = useState<BacktestingResult | null>(null);
  const [riskAlerts, setRiskAlerts] = useState<RiskAlert[]>([]);

  // Dialog states
  const [stressTestDialog, setStressTestDialog] = useState(false);
  const [stressTestResults, setStressTestResults] = useState<StressTestResult | null>(null);
  const [monteCarloDialog, setMonteCarloDialog] = useState(false);
  const [_selectedScenario, _setSelectedScenario] = useState<string>('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [metrics, scenarios, heatmap, alerts, backtesting] = await Promise.all([
        riskAnalyticsService.getRiskMetrics(),
        riskAnalyticsService.getStressTestScenarios(),
        riskAnalyticsService.getRiskHeatmap(),
        riskAnalyticsService.getRiskAlerts(),
        riskAnalyticsService.getBacktestingResults().catch(() => []), // Don't fail if backtesting not available
      ]);

      setRiskMetrics(metrics);
      setStressScenarios(scenarios);
      setHeatmapData(heatmap);
      setRiskAlerts(alerts);
      setBacktestingResults(backtesting);
      
      // Auto-select first backtest if available
      if (backtesting.length > 0 && !selectedBacktest) {
        setSelectedBacktest(backtesting[0]);
      }
    } catch (error) {
      LoggerService.error('Error loading risk analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRunStressTest = async (scenarioId: string) => {
    try {
      setLoading(true);
      const results = await riskAnalyticsService.runStressTest(scenarioId);
      setStressTestResults(results);
      setStressTestDialog(true);
    } catch (error) {
      LoggerService.error('Error running stress test:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMonteCarlo = async () => {
    try {
      setLoading(true);
      const simulation = await riskAnalyticsService.createMonteCarloSimulation({
        name: 'Portfolio Risk Simulation',
        description: 'Monte Carlo analysis of portfolio risk',
        iterations: 10000,
        timeHorizon: 252,
        confidenceLevel: 95,
      });
      setMonteCarloResults(prev => [...prev, simulation]);
      setMonteCarloDialog(false);
    } catch (error) {
      LoggerService.error('Error creating Monte Carlo simulation:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderRiskOverview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Risk Overview Dashboard
        </Typography>
      </Grid>

      {/* Risk Alerts */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Active Risk Alerts
          </Typography>
          {riskAlerts.slice(0, 3).map((alert) => (
            <Alert 
              key={alert.id} 
              severity={alert.severity as 'info' | 'warning' | 'error'} 
              sx={{ mb: 1 }}
              action={
                alert.actionRequired && (
                  <Button size="small" color="inherit">
                    Action Required
                  </Button>
                )
              }
            >
              <AlertTitle>{alert.title}</AlertTitle>
              {alert.description}
            </Alert>
          ))}
        </Paper>
      </Grid>

      {/* Risk Metrics Cards */}
      {riskMetrics.map((metric) => (
        <Grid item xs={12} sm={6} md={4} key={metric.id}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="div">
                  {metric.name}
                </Typography>
                <Chip
                  label={metric.severity}
                  size="small"
                  sx={{ 
                    bgcolor: getSeverityColor(metric.severity),
                    color: 'white',
                  }}
                />
              </Box>
              <Typography variant="h4" sx={{ mb: 1 }}>
                {metric.value.toFixed(2)}
                {metric.category === 'liquidity' || metric.category === 'regulatory' ? '%' : ''}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {metric.change > 0 ? (
                  <TrendingUp color="error" sx={{ mr: 1 }} />
                ) : (
                  <TrendingDown color="success" sx={{ mr: 1 }} />
                )}
                <Typography variant="body2" color={metric.change > 0 ? 'error' : 'success'}>
                  {Math.abs(metric.change).toFixed(2)} from yesterday
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {metric.description}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(metric.value / metric.threshold) * 100}
                sx={{ 
                  mt: 2,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getSeverityColor(metric.severity),
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderStressTesting = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Stress Testing & Scenario Analysis
          </Typography>
          <Button
            variant="contained"
            startIcon={<Assessment />}
            onClick={() => setStressTestDialog(true)}
          >
            Create Custom Scenario
          </Button>
        </Box>
      </Grid>

      {/* Stress Test Scenarios */}
      {stressScenarios.map((scenario) => (
        <Grid item xs={12} md={6} key={scenario.id}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {scenario.name}
                </Typography>
                <Chip
                  label={scenario.severity}
                  size="small"
                  color={scenario.severity === 'extreme' ? 'error' : scenario.severity === 'severe' ? 'warning' : 'info'}
                />
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {scenario.description}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Scenario Parameters:
                </Typography>
                {scenario.parameters.marketShock && (
                  <Typography variant="body2">
                    Market Shock: {scenario.parameters.marketShock}%
                  </Typography>
                )}
                {scenario.parameters.interestRateChange && (
                  <Typography variant="body2">
                    Interest Rate Change: {scenario.parameters.interestRateChange}bp
                  </Typography>
                )}
                {scenario.parameters.creditSpreadChange && (
                  <Typography variant="body2">
                    Credit Spread Change: {scenario.parameters.creditSpreadChange}bp
                  </Typography>
                )}
                <Typography variant="body2">
                  Time Horizon: {scenario.parameters.timeHorizon} days
                </Typography>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                startIcon={<PlayArrow />}
                onClick={() => handleRunStressTest(scenario.id)}
                disabled={loading}
              >
                Run Test
              </Button>
              <Button size="small" startIcon={<Assessment />}>
                View History
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}

      {/* Stress Test Results Dialog */}
      <Dialog 
        open={stressTestDialog} 
        onClose={() => setStressTestDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Stress Test Results</DialogTitle>
        <DialogContent>
          {stressTestResults && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Portfolio Impact
                </Typography>
                <Typography variant="h4" color={stressTestResults.portfolioImpact < 0 ? 'error' : 'success'}>
                  {stressTestResults.portfolioImpact.toFixed(2)}%
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" gutterBottom>
                  VaR Impact
                </Typography>
                <Typography variant="h4">
                  {stressTestResults.varImpact.toFixed(2)}%
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Liquidity Ratio
                </Typography>
                <Typography variant="h4">
                  {stressTestResults.liquidityRatio.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Capital Adequacy
                </Typography>
                <Typography variant="h4">
                  {stressTestResults.capitalAdequacy.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Worst Case Scenario
                </Typography>
                <Alert severity="error">
                  <AlertTitle>Loss: {stressTestResults.worstCaseScenario.loss.toFixed(1)}%</AlertTitle>
                  Probability: {stressTestResults.worstCaseScenario.probability.toFixed(1)}%<br/>
                  {stressTestResults.worstCaseScenario.description}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Mitigation Strategies
                </Typography>
                {stressTestResults.mitigationStrategies.map((strategy, index) => (
                  <Chip key={index} label={strategy} sx={{ m: 0.5 }} />
                ))}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStressTestDialog(false)}>Close</Button>
          <Button variant="contained">Export Results</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );

  const renderRiskHeatmap = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Risk Heatmap & Analysis
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Risk Category</TableCell>
                  <TableCell>Subcategory</TableCell>
                  <TableCell align="center">Risk Level</TableCell>
                  <TableCell align="center">Impact</TableCell>
                  <TableCell align="center">Probability</TableCell>
                  <TableCell>Mitigation</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Due Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {heatmapData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>{row.subcategory}</TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          backgroundColor: getRiskLevelColor(row.riskLevel),
                        }}
                      >
                        {row.riskLevel.toFixed(1)}
                      </Box>
                    </TableCell>
                    <TableCell align="center">{row.impact}</TableCell>
                    <TableCell align="center">{row.probability}</TableCell>
                    <TableCell>{row.mitigation}</TableCell>
                    <TableCell>{row.owner}</TableCell>
                    <TableCell>
                      {row.dueDate ? (
                        <Chip
                          label={format(parseISO(row.dueDate), 'MMM dd')}
                          size="small"
                          color="warning"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderMonteCarloSimulations = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Monte Carlo Simulations
          </Typography>
          <Button
            variant="contained"
            startIcon={<Timeline />}
            onClick={() => setMonteCarloDialog(true)}
          >
            New Simulation
          </Button>
        </Box>
      </Grid>

      {monteCarloResults.map((simulation) => (
        <Grid item xs={12} key={simulation.id}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{simulation.name}</Typography>
              <Chip
                label={simulation.status}
                color={simulation.status === 'completed' ? 'success' : 'info'}
              />
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Simulation Parameters
                  </Typography>
                  <Typography variant="body2">
                    Iterations: {simulation.iterations.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    Time Horizon: {simulation.timeHorizon} days
                  </Typography>
                  <Typography variant="body2">
                    Confidence Level: {simulation.confidenceLevel}%
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption">Expected Return</Typography>
                    <Typography variant="h6">{simulation.results.expectedReturn.toFixed(2)}%</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption">VaR (95%)</Typography>
                    <Typography variant="h6" color="error">{simulation.results.var95.toFixed(2)}%</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption">Sharpe Ratio</Typography>
                    <Typography variant="h6">{simulation.results.sharpeRatio.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption">Volatility</Typography>
                    <Typography variant="h6">{simulation.results.volatility.toFixed(2)}%</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption">Max Drawdown</Typography>
                    <Typography variant="h6" color="error">{simulation.results.maxDrawdown.toFixed(2)}%</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption">Expected Shortfall</Typography>
                    <Typography variant="h6" color="error">{simulation.results.expectedShortfall.toFixed(2)}%</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ height: 300 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Return Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={simulation.results.distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="value" />
                      <YAxis />
                      <RechartsTooltip />
                      <Area type="monotone" dataKey="probability" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ))}

      {/* Monte Carlo Dialog */}
      <Dialog open={monteCarloDialog} onClose={() => setMonteCarloDialog(false)}>
        <DialogTitle>Create Monte Carlo Simulation</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Simulation Name"
            margin="normal"
            defaultValue="Portfolio Risk Simulation"
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={2}
            defaultValue="Monte Carlo analysis of portfolio risk"
          />
          <TextField
            fullWidth
            label="Number of Iterations"
            margin="normal"
            type="number"
            defaultValue={10000}
          />
          <TextField
            fullWidth
            label="Time Horizon (days)"
            margin="normal"
            type="number"
            defaultValue={252}
          />
          <TextField
            fullWidth
            label="Confidence Level (%)"
            margin="normal"
            type="number"
            defaultValue={95}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMonteCarloDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateMonteCarlo} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Create Simulation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Enhanced Risk Analytics
      </Typography>
      
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Speed />} label="Risk Overview" />
          <Tab icon={<Assessment />} label="Stress Testing" />
          <Tab icon={<ShowChart />} label="Risk Heatmap" />
          <Tab icon={<Timeline />} label="Monte Carlo" />
          <Tab icon={<BarChart />} label="Backtesting" />
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        {renderRiskOverview()}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderStressTesting()}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {renderRiskHeatmap()}
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        {renderMonteCarloSimulations()}
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>Backtesting Results</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Historical performance analysis of risk management strategies
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : backtestingResults.length === 0 ? (
          <Alert severity="info">
            <AlertTitle>No Backtesting Results</AlertTitle>
            No backtesting results available. Run a backtest to see historical strategy performance.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {/* Backtesting Results List */}
            <Grid item xs={12} md={selectedBacktest ? 4 : 12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Backtesting Strategies</Typography>
                  {backtestingResults.map((result) => (
                    <Card
                      key={result.id}
                      sx={{
                        mb: 2,
                        cursor: 'pointer',
                        border: selectedBacktest?.id === result.id ? 2 : 1,
                        borderColor: selectedBacktest?.id === result.id ? 'primary.main' : 'divider',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => setSelectedBacktest(result)}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {result.strategy}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {format(parseISO(result.startDate), 'MMM dd, yyyy')} - {format(parseISO(result.endDate), 'MMM dd, yyyy')}
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Chip
                            label={`Return: ${result.totalReturn.toFixed(2)}%`}
                            color={result.totalReturn > 0 ? 'success' : 'error'}
                            size="small"
                          />
                          <Chip
                            label={`Sharpe: ${result.sharpeRatio.toFixed(2)}`}
                            color={result.sharpeRatio > 1 ? 'success' : 'default'}
                            size="small"
                          />
                          <Chip
                            label={`Max DD: ${result.maxDrawdown.toFixed(2)}%`}
                            color={result.maxDrawdown > -10 ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* Selected Backtest Details */}
            {selectedBacktest && (
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                      <Typography variant="h6">{selectedBacktest.strategy}</Typography>
                      <Button
                        size="small"
                        onClick={() => setSelectedBacktest(null)}
                      >
                        Close
                      </Button>
                    </Box>

                    {/* Performance Metrics */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Total Return</Typography>
                          <Typography variant="h6" color={selectedBacktest.totalReturn > 0 ? 'success.main' : 'error.main'}>
                            {selectedBacktest.totalReturn.toFixed(2)}%
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Annualized Return</Typography>
                          <Typography variant="h6" color={selectedBacktest.annualizedReturn > 0 ? 'success.main' : 'error.main'}>
                            {selectedBacktest.annualizedReturn.toFixed(2)}%
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Sharpe Ratio</Typography>
                          <Typography variant="h6" color={selectedBacktest.sharpeRatio > 1 ? 'success.main' : 'default'}>
                            {selectedBacktest.sharpeRatio.toFixed(2)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Max Drawdown</Typography>
                          <Typography variant="h6" color={selectedBacktest.maxDrawdown > -10 ? 'success.main' : 'error.main'}>
                            {selectedBacktest.maxDrawdown.toFixed(2)}%
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Volatility</Typography>
                          <Typography variant="h6">{selectedBacktest.volatility.toFixed(2)}%</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Win Rate</Typography>
                          <Typography variant="h6" color={selectedBacktest.winRate > 50 ? 'success.main' : 'default'}>
                            {selectedBacktest.winRate.toFixed(1)}%
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Profit Factor</Typography>
                          <Typography variant="h6" color={selectedBacktest.profitFactor > 1 ? 'success.main' : 'error.main'}>
                            {selectedBacktest.profitFactor.toFixed(2)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Performance Chart */}
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                      Performance vs Benchmark
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={selectedBacktest.performanceChart}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => format(parseISO(value), 'MMM yyyy')}
                        />
                        <YAxis />
                        <RechartsTooltip
                          labelFormatter={(value) => format(parseISO(value), 'MMM dd, yyyy')}
                        />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#2196f3"
                          fill="#2196f3"
                          fillOpacity={0.3}
                          name="Strategy"
                        />
                        <Area
                          type="monotone"
                          dataKey="benchmark"
                          stroke="#757575"
                          fill="#757575"
                          fillOpacity={0.2}
                          name="Benchmark"
                        />
                      </AreaChart>
                    </ResponsiveContainer>

                    {/* Trades Table */}
                    {selectedBacktest.trades.length > 0 && (
                      <>
                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                          Trade History
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Symbol</TableCell>
                                <TableCell align="right">Quantity</TableCell>
                                <TableCell align="right">Price</TableCell>
                                <TableCell align="right">P&L</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedBacktest.trades.map((trade, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>{format(parseISO(trade.date), 'MMM dd, yyyy')}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={trade.action.toUpperCase()}
                                      color={trade.action === 'buy' ? 'primary' : 'secondary'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>{trade.symbol}</TableCell>
                                  <TableCell align="right">{trade.quantity}</TableCell>
                                  <TableCell align="right">${trade.price.toFixed(2)}</TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      color={trade.pnl > 0 ? 'success.main' : trade.pnl < 0 ? 'error.main' : 'text.primary'}
                                    >
                                      ${trade.pnl.toFixed(2)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </TabPanel>
    </Box>
  );
};

export default EnhancedRiskAnalytics;